import React, { useState, useEffect } from "react";
import { AlertCircle, FileText, Loader2 } from "lucide-react";
import { DocumentParser, type DocumentData } from "./xml-document-parser.service";
import { KvDocumentViewer } from "./components/kv-document-viewer.component";
import { LsDocumentViewer } from "./components/ls-document-viewer.component";

interface XmlDocumentViewerProps {
	url: string;
}

export const XmlDocumentViewer: React.FC<XmlDocumentViewerProps> = ({ url }) => {
	const [documentData, setDocumentData] = useState<DocumentData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>("");

	useEffect(() => {
		const fetchAndParseDocument = async () => {
			try {
				setLoading(true);
				setError("");

				const response = await fetch(url);
				if (!response.ok) {
					throw new Error("Fehler beim Laden des Dokuments");
				}

				const xmlText = await response.text();
				const parsed = DocumentParser.parseXmlDocument(xmlText);

				if (!parsed) {
					throw new Error("Dokument konnte nicht verarbeitet werden");
				}

				setDocumentData(parsed);
			} catch (err) {
				console.error("Error loading document:", err);
				setError(err instanceof Error ? err.message : "Unbekannter Fehler");
			} finally {
				setLoading(false);
			}
		};

		fetchAndParseDocument();
	}, [url]);

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full bg-gray-100">
				<div className="text-center">
					<Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
					<p className="text-gray-600">Dokument wird geladen...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-full bg-gray-100">
				<div className="text-center bg-red-50 rounded-lg p-8 max-w-md">
					<AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
					<h3 className="text-lg font-semibold text-gray-900 mb-1">
						Fehler beim Laden
					</h3>
					<p className="text-gray-600">{error}</p>
				</div>
			</div>
		);
	}

	if (!documentData) {
		return (
			<div className="flex items-center justify-center h-full bg-gray-100">
				<div className="text-center">
					<FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
					<p className="text-gray-600">Kein Dokument verfügbar</p>
				</div>
			</div>
		);
	}

	const renderDocument = () => {
		switch (documentData.type) {
			case 'kostenvoranschlag':
				return <KvDocumentViewer data={documentData} />;
			case 'lieferschein':
				return <LsDocumentViewer data={documentData} />;
			default:
				return (
					<div className="text-center p-8">
						<FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
						<p className="text-gray-600">Unbekannter Dokumenttyp: {documentData.type}</p>
					</div>
				);
		}
	};

	return (
		<div className="h-full bg-gray-100 overflow-auto">
			{/* A4 Container */}
			<div className="max-w-[210mm] mx-auto">
				<div className="bg-white shadow-lg mt-12">
					<div className="text-center p-4 bg-white">
						<div className="bg-gray-200 p-2 text-sm">
							Dieses Dokument ist ausschliesslich eine Vorschau des XML. Es kann von den PDFs anderer Systeme abweichen.
							{documentData.version !== 'unknown' && (
								<span className="ml-1">Standard: {documentData.version === '500' ? '5.00' : '4.50'}</span>
							)}
						</div>
					</div>
					<div className="p-8">
						{renderDocument()}
					</div>
				</div>
				{/* Add some bottom margin */}
				<div className="h-12"></div>
			</div>
		</div>
	);
};